import { supabase } from "@/integrations/supabase/client";
import type { QrType } from "@/hooks/useRotatingQrToken";

export interface ResolveScannedResult {
  ok: boolean;
  /** Static barcode value to feed into the existing `.eq("barcode_*", ...)` lookups. */
  resolvedCode: string | null;
  studentId: string | null;
  legacy: boolean;
  reason?: string;
  message?: string;
}

/**
 * Wraps a scanned QR string. If it's a rotating v1 token, calls the
 * `verify-student-qr` edge function to validate signature, TTL and replay,
 * then returns the student's static barcode value so existing scanner
 * queries continue to work unchanged. Legacy static codes pass through.
 *
 * Reasons the caller can show as a toast:
 * - `expired` — likely a screenshot or photo
 * - `replayed` — token already used
 * - `invalid_signature` — tampered
 * - `legacy_disabled` — superadmin disabled static codes
 */
export async function resolveScannedQr(
  rawCode: string,
  qrType: QrType,
  scannerContext?: string,
): Promise<ResolveScannedResult> {
  const code = (rawCode || "").trim();
  if (!code) {
    return { ok: false, resolvedCode: null, studentId: null, legacy: false, reason: "empty" };
  }

  // Cheap fast-path: if it's not a signed token (v1 server-minted or v2 TOTP),
  // just return it as-is. Both v1 and v2 must go through the edge function so
  // the signature, TTL and replay checks run server-side.
  if (!code.startsWith("v1.") && !code.startsWith("v2.")) {
    return { ok: true, resolvedCode: code, studentId: null, legacy: true };
  }

  try {
    const { data, error } = await supabase.functions.invoke("verify-student-qr", {
      body: { code, qr_type: qrType, scanner_context: scannerContext ?? null },
    });
    if (error) {
      return {
        ok: false,
        resolvedCode: null,
        studentId: null,
        legacy: false,
        reason: "verify_failed",
        message: error.message,
      };
    }
    return {
      ok: !!data?.ok,
      resolvedCode: data?.resolved_code ?? null,
      studentId: data?.student_id ?? null,
      legacy: !!data?.legacy,
      reason: data?.reason,
      message: data?.message,
    };
  } catch (e) {
    return {
      ok: false,
      resolvedCode: null,
      studentId: null,
      legacy: false,
      reason: "verify_failed",
      message: (e as Error).message,
    };
  }
}
